/**
 * Guardian (family portal) shell (Phase 12, plan §4.1).
 *
 * Rendered by App instead of the staff dashboard when the user holds portal:read and no staff
 * permission. Every read goes through /portal/* (allow-list DTOs).
 *
 * Gate codes (revision 2026-10-10): only PORTAL_UNAVAILABLE (tenant-wide) blanks the shell. A
 * per-patient code (PORTAL_ACCESS_ENDED / PORTAL_PENDING_APPROVAL) from a read marks that one
 * patient as no longer active, re-reads /portal/me once, and keeps the guardian's other ACTIVE
 * patients; stateFromMe then decides whether a state screen is needed.
 */
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  CalendarDays, ClipboardPlus, FileText, Home, Loader, LogOut, Menu, Phone, Syringe, TrendingUp, User, Users, X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import iconWhite from '../../assets/images/icon_white.png';
import PortalService, { portalGateCode } from '../../services/portal/portal.service';
import type { PortalPatient, PortalGateCode, PortalLinkStatus, PortalMe } from '../../dtos/portal/portal.dto';
import ReadOnlyBanner from './ReadOnlyBanner';
import PortalStateScreen, { PortalState } from './PortalStateScreen';
import { PortalGateContext } from './usePortalResource';
import { FOCUS_RING } from './format';
import PortalAvatar from './PortalAvatar';
import { useGuardianPhoto, usePatientPhoto } from './usePortalPhotos';
import HomeScreen from './screens/HomeScreen';
import ScheduleScreen from './screens/ScheduleScreen';
import SummariesScreen from './screens/SummariesScreen';
import CarePlanScreen from './screens/CarePlanScreen';
import MedicationsScreen from './screens/MedicationsScreen';
import GrowthScreen from './screens/GrowthScreen';
import CareTeamScreen from './screens/CareTeamScreen';
import PatientDetailsScreen from './screens/PatientDetailsScreen';
import ContactScreen from './screens/ContactScreen';
import PersonaBar from '../demo/PersonaBar';

export type PortalScreenKey =
  | 'home'
  | 'schedule'
  | 'summaries'
  | 'careplan'
  | 'medications'
  | 'growth'
  | 'careteam'
  | 'about'
  | 'contact';

export const PORTAL_SCREENS: { key: PortalScreenKey; label: string; Icon: LucideIcon }[] = [
  { key: 'home', label: 'Home', Icon: Home },
  { key: 'schedule', label: 'Schedule', Icon: CalendarDays },
  { key: 'summaries', label: 'Visit summaries', Icon: FileText },
  { key: 'careplan', label: 'Care plan', Icon: ClipboardPlus },
  { key: 'medications', label: 'Medications', Icon: Syringe },
  { key: 'growth', label: 'Growth', Icon: TrendingUp },
  { key: 'careteam', label: 'Care team', Icon: Users },
  { key: 'about', label: 'Patient details', Icon: User },
  { key: 'contact', label: 'Contact agency', Icon: Phone },
];

const GATE_TO_STATE: Record<Exclude<PortalGateCode, 'FORBIDDEN'>, PortalState> = {
  PORTAL_PENDING_APPROVAL: 'awaiting',
  PORTAL_ACCESS_ENDED: 'ended',
  PORTAL_UNAVAILABLE: 'unavailable',
};

/** Which state screen /portal/me calls for, or null when at least one patient is ACTIVE. */
export function stateFromMe(me: PortalMe): PortalState | null {
  if (!me.portalAvailable) return 'unavailable';
  if (me.patients.some((c) => c.linkStatus === 'ACTIVE')) return null;
  if (me.patients.length === 0 || me.patients.some((c) => c.linkStatus === 'PENDING_APPROVAL')) return 'awaiting';
  return 'ended';
}

/** Link status a per-patient gate code implies for that patient. */
const GATE_TO_LINK_STATUS: Record<'PORTAL_PENDING_APPROVAL' | 'PORTAL_ACCESS_ENDED', PortalLinkStatus> = {
  PORTAL_PENDING_APPROVAL: 'PENDING_APPROVAL',
  PORTAL_ACCESS_ENDED: 'ENDED',
};

/** Overlay per-patient gates seen on reads onto /portal/me (a gate is never undone client-side). */
export function applyGates(me: PortalMe, gated: Record<string, PortalLinkStatus>): PortalMe {
  if (Object.keys(gated).length === 0) return me;
  return {
    ...me,
    patients: me.patients.map((p) =>
      p.linkStatus === 'ACTIVE' && gated[p.patientId] ? { ...p, linkStatus: gated[p.patientId] } : p
    ),
  };
}

/** Shown when one patient's link ends while others stay active. No PHI. */
export const ACCESS_ENDED_NOTICE = 'Your access to one patient’s record has ended.';

interface GuardianShellProps {
  onSignOut: () => void | Promise<void>;
}

const GuardianShell: React.FC<GuardianShellProps> = ({ onSignOut }) => {
  const switcherId = useId();
  const [me, setMe] = useState<PortalMe | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [gateState, setGateState] = useState<PortalState | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [screen, setScreen] = useState<PortalScreenKey>('home');
  /** patientId → link status implied by a per-patient gate code seen on a read */
  const [gated, setGated] = useState<Record<string, PortalLinkStatus>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const refreshing = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    PortalService.getMe()
      .then((data) => {
        if (cancelled) return;
        setMe(data);
        const firstActive = data.patients.find((c) => c.linkStatus === 'ACTIVE');
        setSelectedId(firstActive?.patientId ?? null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const code = portalGateCode(err);
        if (code && code !== 'FORBIDDEN') setGateState(GATE_TO_STATE[code]);
        else setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** Re-read /portal/me after a per-patient gate. At most one request in flight; never retried. */
  const refreshMe = useCallback(() => {
    if (refreshing.current) return;
    refreshing.current = true;
    PortalService.getMe()
      .then((data) => {
        if (mounted.current) setMe(data);
      })
      .catch((err: unknown) => {
        if (!mounted.current) return;
        const code = portalGateCode(err);
        // a guardian-wide gate on /portal/me itself still switches the whole shell;
        // any other failure keeps the locally gated view
        if (code && code !== 'FORBIDDEN') setGateState(GATE_TO_STATE[code]);
      })
      .finally(() => {
        refreshing.current = false;
      });
  }, []);

  const onGate = useCallback(
    (code: PortalGateCode, gatedPatientId?: string) => {
      if (code === 'FORBIDDEN') return;
      if (code === 'PORTAL_UNAVAILABLE' || !gatedPatientId) {
        setGateState(GATE_TO_STATE[code]);
        return;
      }
      const status = GATE_TO_LINK_STATUS[code];
      setGated((prev) => (prev[gatedPatientId] === status ? prev : { ...prev, [gatedPatientId]: status }));
      if (code === 'PORTAL_ACCESS_ENDED') setNotice(ACCESS_ENDED_NOTICE);
      refreshMe();
    },
    [refreshMe]
  );

  const effectiveMe = useMemo(() => (me ? applyGates(me, gated) : null), [me, gated]);
  const activePatients: PortalPatient[] = useMemo(
    () => (effectiveMe ? effectiveMe.patients.filter((c) => c.linkStatus === 'ACTIVE') : []),
    [effectiveMe]
  );
  const pendingCount = effectiveMe
    ? effectiveMe.patients.filter((c) => c.linkStatus === 'PENDING_APPROVAL').length
    : 0;
  const selected = activePatients.find((c) => c.patientId === selectedId) ?? activePatients[0] ?? null;
  const guardianPhoto = useGuardianPhoto();
  const patientPhoto = usePatientPhoto(selected?.patientId ?? null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const handleSignOut = () => {
    void onSignOut();
  };

  const state = gateState ?? (effectiveMe ? stateFromMe(effectiveMe) : null);
  if (state) return <PortalStateScreen state={state} onSignOut={handleSignOut} />;

  if (loadError) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <section className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-slate-900">
            <img src={iconWhite} alt="Pelora Health" className="h-7 w-7" />
          </div>
          <h1 className="text-xl font-semibold text-slate-900">Something went wrong</h1>
          <p role="alert" className="mt-3 text-slate-700">The family portal could not be loaded. Please try again later.</p>
          <button
            type="button"
            onClick={handleSignOut}
            className={`mt-6 inline-flex min-h-11 items-center gap-2 rounded-md bg-slate-800 px-5 text-sm font-medium text-white hover:bg-slate-700 ${FOCUS_RING}`}
          >
            <LogOut size={16} aria-hidden="true" /> Sign out
          </button>
        </section>
      </main>
    );
  }

  if (!me || !selected) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-50" aria-busy="true">
        <div role="status" aria-live="polite" className="flex flex-col items-center gap-2">
          <Loader size={32} className="animate-spin text-teal-700" aria-hidden="true" />
          <span className="text-sm text-slate-700">Loading</span>
        </div>
      </main>
    );
  }

  const patientId = selected.patientId;
  const selectedName = `${selected.firstName} ${selected.lastName ?? ''}`.trim();
  const guardianName = `${me.guardian.firstName} ${me.guardian.lastName}`.trim();
  const chooseScreen = (key: PortalScreenKey) => {
    setScreen(key);
    setMenuOpen(false);
  };
  const renderScreen = () => {
    switch (screen) {
      case 'home':
        return (
          <HomeScreen
            patientId={patientId}
            patientName={selected.firstName}
            patientFullName={selectedName}
            patientPictureUrl={patientPhoto}
          />
        );
      case 'schedule':
        return <ScheduleScreen patientId={patientId} />;
      case 'summaries':
        return <SummariesScreen patientId={patientId} />;
      case 'careplan':
        return <CarePlanScreen patientId={patientId} />;
      case 'medications':
        return <MedicationsScreen patientId={patientId} />;
      case 'growth':
        return <GrowthScreen patientId={patientId} />;
      case 'careteam':
        return <CareTeamScreen patientId={patientId} />;
      case 'about':
        return <PatientDetailsScreen patientId={patientId} />;
      case 'contact':
        return <ContactScreen patientId={patientId} />;
      default:
        return null;
    }
  };

  return (
    <PortalGateContext.Provider value={onGate}>
      {/* Demo sandbox: shown when the prospect views the portal as the seeded guardian */}
      <PersonaBar />
      <div className="min-h-screen bg-slate-50 text-slate-900 lg:flex">
        <a
          href="#portal-main"
          className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-[60] focus:rounded focus:bg-white focus:px-3 focus:py-2 focus:text-slate-900"
        >
          Skip to content
        </a>

        {/* Mobile top bar (the sidebar below becomes a drawer under lg) */}
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between bg-slate-900 px-3 text-white lg:hidden">
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
            aria-expanded={menuOpen}
            aria-controls="portal-nav-panel"
            className={`-ml-1 inline-flex h-11 w-11 items-center justify-center rounded-lg hover:bg-slate-700 ${FOCUS_RING}`}
          >
            <Menu size={24} aria-hidden="true" />
          </button>
          <div className="flex items-center gap-2">
            <img src={iconWhite} alt="" className="h-6 w-6" />
            <span className="text-lg font-semibold">Pelora Health</span>
          </div>
          <PortalAvatar name={guardianName} pictureUrl={guardianPhoto} size="sm" tone="dark" />
        </header>

        {menuOpen && (
          <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setMenuOpen(false)} aria-hidden="true" />
        )}

        <aside
          id="portal-nav-panel"
          className={`fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col bg-slate-900 text-white shadow-xl transition-transform duration-200 lg:sticky lg:top-0 lg:z-20 lg:h-screen lg:w-64 lg:max-w-none lg:shrink-0 lg:translate-x-0 lg:shadow-none ${
            menuOpen ? 'translate-x-0' : 'invisible -translate-x-full lg:visible'
          }`}
        >
          <div className="flex h-16 items-center justify-between border-b border-slate-700 px-4">
            <div className="flex items-center gap-2">
              <img src={iconWhite} alt="" className="h-7 w-7" />
              <span className="text-xl font-semibold">Pelora Health</span>
            </div>
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-slate-300 hover:bg-slate-700 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400 lg:hidden"
            >
              <X size={22} aria-hidden="true" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto overflow-x-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="border-b border-slate-800 p-4">
              <div className="flex items-center gap-3 rounded-xl border border-slate-700 bg-slate-800 p-3">
                <PortalAvatar name={selectedName} pictureUrl={patientPhoto} size="lg" tone="dark" />
                <div className="min-w-0">
                  <p className="truncate font-semibold text-white">{selectedName}</p>
                  <p className="text-xs text-slate-300">{selected.paused ? 'Visits paused' : 'Receiving care'}</p>
                </div>
              </div>
              {activePatients.length > 1 && (
                <div className="mt-3">
                  <label htmlFor={switcherId} className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-300">
                    Patient
                  </label>
                  <select
                    id={switcherId}
                    value={selected.patientId}
                    onChange={(e) => {
                      setSelectedId(e.target.value);
                      setNotice(null);
                      setMenuOpen(false);
                    }}
                    className="min-h-11 w-full rounded-lg border border-slate-600 bg-slate-800 px-2 text-sm text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400"
                  >
                    {activePatients.map((patient) => (
                      <option key={patient.patientId} value={patient.patientId}>
                        {`${patient.firstName} ${patient.lastName ?? ''}`.trim()}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <nav aria-label="Family portal" className="p-3">
              <ul className="space-y-1">
                {PORTAL_SCREENS.map(({ key, label, Icon }) => {
                  const active = screen === key;
                  return (
                    <li key={key}>
                      <button
                        type="button"
                        onClick={() => chooseScreen(key)}
                        aria-current={active ? 'page' : undefined}
                        className={`flex min-h-11 w-full items-center gap-3 rounded-lg px-3 text-left text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400 ${
                          active ? 'bg-slate-700 font-semibold text-white' : 'text-gray-200 hover:bg-slate-700'
                        }`}
                      >
                        <Icon size={18} aria-hidden="true" className={active ? 'text-teal-300' : 'text-gray-400'} />
                        <span>{label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>

          <div className="border-t border-slate-700 p-4">
            <div className="flex items-center gap-3">
              <PortalAvatar name={guardianName} pictureUrl={guardianPhoto} size="md" tone="dark" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-200">{guardianName || 'Signed in'}</p>
                <p className="truncate text-xs text-gray-400">{me.guardian.email}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleSignOut}
              className="mt-3 flex min-h-11 w-full items-center gap-2 rounded-lg px-3 text-sm text-gray-200 hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400"
            >
              <LogOut size={18} aria-hidden="true" /> Sign out
            </button>
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <ReadOnlyBanner />
          {notice && (
            <p role="status" className="mx-auto max-w-5xl px-4 pt-4 text-sm font-medium text-slate-800 sm:px-6">
              {notice}
            </p>
          )}
          {pendingCount > 0 && (
            <p role="status" className="mx-auto max-w-5xl px-4 pt-4 text-sm text-slate-700 sm:px-6">
              {pendingCount === 1 ? 'One more patient link is' : `${pendingCount} more patient links are`} awaiting approval by the agency.
            </p>
          )}
          <main id="portal-main" tabIndex={-1} className="focus:outline-none">
            {/* key: switching patient remounts the screen so nothing from the other patient lingers */}
            <React.Fragment key={patientId}>{renderScreen()}</React.Fragment>
          </main>
        </div>
      </div>
    </PortalGateContext.Provider>
  );
};

export default GuardianShell;
